import { DeviceState } from "@devices/types";
import { FileInfo } from "@files/types";

import { hasAnalyzerInProgress, hasApkMetadata, hasFailedAnalyzer, isApk } from "./index";

export type ApkFileStatus =
    | "toInstall"
    | "toUpdate"
    | "notApk"
    | "installed"
    | "invalidApk"
    | "processing"
    | "analyzerFailed"
    | "currentVersion"
    | "outdatedVersion";

export type InstalledVersionStatus = "toInstall" | "toUpdate" | "currentVersion" | "outdatedVersion";

/**
 * The checks that do not depend on any device: a running analyzer, a non-APK file or missing manifest
 * metadata rule a file out for every device at once. Returns null when only the per-device version
 * comparison is left, so a group-wide caller can run these once instead of per device.
 */
export const getFileLevelApkStatus = (file: FileInfo): ApkFileStatus | null => {
    if (hasAnalyzerInProgress(file)) {
        return "processing";
    }
    if (hasFailedAnalyzer(file)) {
        return "analyzerFailed";
    }
    if (!isApk(file.filename)) {
        return "notApk";
    }
    if (!hasApkMetadata(file)) {
        return "invalidApk";
    }
    return null;
};

const findInstalledPackage = (file: FileInfo, packages?: DeviceState["packages"] | null) =>
    packages?.find((item) => item.package.toLowerCase() === file.metadata?.file?.apk?.manifest?.package.toLowerCase());

/**
 * The single source of truth for how an installed version relates to an uploaded one. Callers that
 * already resolved the installed version code (e.g. from a prebuilt group-wide index) use this
 * directly instead of re-scanning a package list.
 */
export const compareVersionCodes = (
    installedVersionCode: number | undefined,
    targetVersionCode: number,
): InstalledVersionStatus => {
    if (installedVersionCode === undefined) {
        return "toInstall";
    }
    if (installedVersionCode < targetVersionCode) {
        return "toUpdate";
    }
    if (installedVersionCode === targetVersionCode) {
        return "currentVersion";
    }

    return "outdatedVersion";
};

const compareToInstalledPackages = (file: FileInfo, packages?: DeviceState["packages"] | null): InstalledVersionStatus => {
    const installedPackage = findInstalledPackage(file, packages);

    return compareVersionCodes(
        installedPackage ? Number(installedPackage.versionCode) : undefined,
        Number(file.metadata?.file?.apk?.manifest?.versioncode),
    );
};

export const getApkFileStatus = (file: FileInfo, packages?: DeviceState["packages"] | null): ApkFileStatus =>
    getFileLevelApkStatus(file) ?? compareToInstalledPackages(file, packages);
